// Regulation Detail Page
// Generated for Task 12.2 - Create regulations management frontend

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  BellIcon,
  ClockIcon,
  DocumentTextIcon,
  TagIcon,
  MapPinIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import RegulationsService from '../services/regulationsService';
import { Regulation, RegulationHistory } from '../types/regulation';

// Simple toast replacement (react-hot-toast not installed)
const toast = {
  error: (msg: string) => console.error('[Toast]', msg),
  success: (msg: string) => console.log('[Toast]', msg),
};

const RegulationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [regulation, setRegulation] = useState<Regulation | null>(null);
  const [history, setHistory] = useState<RegulationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadRegulation();
      loadHistory();
    }
  }, [id]);

  const loadRegulation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await RegulationsService.getRegulation(Number(id));
      setRegulation(response.regulation);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement de la réglementation');
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const historyData = await RegulationsService.getHistory(Number(id));
      setHistory(historyData);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await RegulationsService.deleteRegulation(Number(id));
      toast.success('Réglementation supprimée avec succès');
      navigate('/regulations');
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleActivate = async () => {
    try {
      const updated = await RegulationsService.activateRegulation(Number(id));
      setRegulation(updated);
      toast.success('Réglementation activée');
      loadHistory();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de l\'activation');
    }
  };

  const handleSuspend = async () => {
    try {
      const updated = await RegulationsService.suspendRegulation(Number(id));
      setRegulation(updated);
      toast.success('Réglementation suspendue');
      loadHistory();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la suspension');
    }
  };

  const handleNotify = async () => {
    try {
      await RegulationsService.notifyHotels(Number(id));
      toast.success('Notifications envoyées');
      setShowNotifyModal(false);
      loadRegulation();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de l\'envoi des notifications');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !regulation) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-800">{error || 'Réglementation non trouvée'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/regulations"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Retour à la liste
          </Link>
        </div>
        
        <div className="flex items-center space-x-3">
          {regulation.status === 'draft' && (
            <button
              onClick={handleActivate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              Activer
            </button>
          )}
          
          {regulation.status === 'active' && (
            <>
              <button
                onClick={() => setShowNotifyModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <BellIcon className="h-5 w-5 mr-2" />
                Notifier les hôtels
              </button>
              
              <button
                onClick={handleSuspend}
                className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-white hover:bg-orange-50"
              >
                Suspendre
              </button>
            </>
          )}
          
          {regulation.status === 'suspended' && (
            <button
              onClick={handleActivate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              Réactiver
            </button>
          )}
          
          <Link
            to={`/regulations/${id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Modifier
          </Link>
          
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
          >
            <TrashIcon className="h-5 w-5 mr-2" />
            Supprimer
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{regulation.title}</h1>
              <p className="mt-1 text-sm text-gray-500">
                Version {regulation.version} • Créée le {new Date(regulation.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                regulation.status === 'active' ? 'bg-green-100 text-green-800' :
                regulation.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                regulation.status === 'expired' ? 'bg-red-100 text-red-800' :
                regulation.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {RegulationsService.getStatusLabel(regulation.status)}
              </span>
              
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium border ${
                regulation.priority === 'critical' ? 'text-red-600 bg-red-50 border-red-200' :
                regulation.priority === 'high' ? 'text-orange-600 bg-orange-50 border-orange-200' :
                regulation.priority === 'medium' ? 'text-yellow-600 bg-yellow-50 border-yellow-200' :
                'text-green-600 bg-green-50 border-green-200'
              }`}>
                {RegulationsService.getPriorityLabel(regulation.priority)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Description
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{regulation.description}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2" />
                Portée
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {RegulationsService.getScopeLabel(regulation.scope)}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <TagIcon className="h-5 w-5 mr-2" />
                Catégorie
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{regulation.category}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <ClockIcon className="h-5 w-5 mr-2" />
                Dates
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                <div>Effective: {new Date(regulation.effective_date).toLocaleDateString('fr-FR')}</div>
                {regulation.expiry_date && (
                  <div>Expire: {new Date(regulation.expiry_date).toLocaleDateString('fr-FR')}</div>
                )}
              </dd>
            </div>

            {regulation.tags && regulation.tags.length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Tags</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  {regulation.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                </dd>
              </div>
            )}

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Contenu</dt>
              <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-md">
                {regulation.content}
              </dd>
            </div>

            {regulation.notification_status && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Statut des notifications</dt>
                <dd className="mt-1">
                  <div className="bg-blue-50 p-4 rounded-md">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-blue-600">
                          {regulation.notification_status.total_hotels}
                        </div>
                        <div className="text-sm text-gray-600">Total hôtels</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">
                          {regulation.notification_status.notified_hotels}
                        </div>
                        <div className="text-sm text-gray-600">Notifiés</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-600">
                          {regulation.notification_status.acknowledged_hotels}
                        </div>
                        <div className="text-sm text-gray-600">Confirmés</div>
                      </div>
                    </div>
                  </div>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h2 className="text-lg font-medium text-gray-900">Historique des modifications</h2>
          </div>
          <div className="border-t border-gray-200">
            <ul className="divide-y divide-gray-200">
              {history.map((entry) => (
                <li key={entry.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {entry.action}
                        </span>
                        <span className="text-sm text-gray-500">
                          Version {entry.version}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {new Date(entry.changed_at).toLocaleString('fr-FR')}
                      </div>
                      {entry.comment && (
                        <div className="mt-1 text-sm text-gray-600 italic">
                          {entry.comment}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Confirmer la suppression
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer cette réglementation ? Cette action est irréversible.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notify Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <BellIcon className="mx-auto h-12 w-12 text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Notifier les hôtels
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Envoyer une notification à tous les hôtels concernés par cette réglementation ?
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setShowNotifyModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400"
                >
                  Annuler
                </button>
                <button
                  onClick={handleNotify}
                  className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-blue-700"
                >
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulationDetailPage;
